import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  IconButton,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "../assets/img/LeftIcon.svg";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import { useAppSelector, useAppDispatch } from "hooks";
import { Note } from "../types/supervisorType";
import UserAvatar from "./UserAvatar";
import { TypingIndicator } from "../shared/components";
import SupervisorAction from "../actions/supervisorAction";
import { ActionTypes } from "../store/actionTypes";
import TicketQualityReviewPanel from "./TicketQualityReviewAction";
import TicketDeviceProperties from "./TicketDeviceProperties";
import TicketDetailSection from "./TicketDetailSection";

// Helper to render HTML content safely, preserving newlines
const renderHtmlContent = (htmlString: string) => {
  if (!htmlString) return null;
  
  // Check if content contains HTML tags
  const hasHtmlTags = /<[^>]+>/g.test(htmlString);
  
  // If it's plain text, convert newlines to <br> tags
  if (!hasHtmlTags) {
    const formattedContent = htmlString
      .split('\n')
      .map((line, index, array) => {
        // Don't add <br> after the last line
        return index < array.length - 1 ? `${line}<br />` : line;
      })
      .join('');
    return <div dangerouslySetInnerHTML={{ __html: formattedContent }} />;
  }
  
  // If it's HTML, preserve as-is but ensure newlines are preserved
  // Replace newlines in HTML with <br> tags (but be careful not to break existing HTML)
  const formattedHtml = htmlString.replace(/\n/g, '<br />');
  return <div dangerouslySetInnerHTML={{ __html: formattedHtml }} />;
};

const wrappedHtmlContentSx = {
  minWidth: 0,
  maxWidth: "100%",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  whiteSpace: "normal",
  "& *": {
    minWidth: 0,
    maxWidth: "100%",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  "& a": {
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  "& img, & video, & iframe": {
    maxWidth: "100%",
    height: "auto",
  },
  "& pre, & code": {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  "& table": {
    width: "100%",
    maxWidth: "100%",
    tableLayout: "fixed",
  },
  "& th, & td": {
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
} as const;

// Helper to get note visibility badge info based on publish value
const getNoteVisibilityBadge = (publish?: number | null) => {
  if (publish === 4) {
    return { label: "internal", color: "#B0BEC5" as const };
  } else if (publish === 1) {
    return { label: "external", color: "#81C784" as const };
  } else {
    return { label: "other", color: "#BDBDBD" as const };
  }
};

interface NinjaOneTicketDetailProps {
  ticketId: string;
  onBack: () => void;
  onCollapseForChat?: () => void;
  chatPlacement?: "right" | "below";
  compactSidePane?: boolean;
}

const NinjaOneTicketDetail: React.FC<NinjaOneTicketDetailProps> = ({
  ticketId,
  onBack,
  onCollapseForChat,
  chatPlacement = "right",
  compactSidePane = false,
}) => {
  const {
    selectedTicket,
    isLoading,
    error,
    ninjaoneUsers = [],
    isRefreshing,
  } = useAppSelector((state) => state.supervisor);
  const currentUser = useAppSelector((state: any) => state.auth.user);
  const dispatch = useAppDispatch();
  const [propertiesExpanded, setPropertiesExpanded] = useState(!compactSidePane);
  const [localRefreshing, setLocalRefreshing] = useState(false);

  useEffect(() => {
    setPropertiesExpanded(!compactSidePane);
  }, [compactSidePane]);

  // Watch for refresh trigger and re-fetch ticket data silently (without global loading)
  useEffect(() => {
    if (isRefreshing && ticketId && currentUser?.email && !localRefreshing) {
      setLocalRefreshing(true);
      dispatch(SupervisorAction.refreshTicketById(ticketId, currentUser.email) as any)
        .then(() => {
          dispatch({ type: ActionTypes.TICKET_REFRESHING, payload: false });
          setLocalRefreshing(false);
        })
        .catch(() => {
          dispatch({ type: ActionTypes.TICKET_REFRESHING, payload: false });
          setLocalRefreshing(false);
        });
    }
  }, [isRefreshing, ticketId, currentUser?.email, dispatch, localRefreshing]);

  // NinjaOne ticket is likely at selectedTicket.ticket
  const ticket = selectedTicket?.ticket || selectedTicket;
  const conversations = React.useMemo(
    () => selectedTicket?.conversations || ticket?.conversations || [],
    [selectedTicket?.conversations, ticket?.conversations]
  );

  const notes = React.useMemo(
    () => selectedTicket?.notes || ticket?.notes || [],
    [selectedTicket?.notes, ticket?.notes]
  );

  // Debug logging
  React.useEffect(() => {
    console.log("=== NinjaOneTicketDetail Debug ===");
    console.log("Selected ticket:", selectedTicket);
    console.log("Ticket:", ticket);
    console.log("Conversations:", conversations);
    console.log("NinjaOne users:", ninjaoneUsers);
    console.log("===============================");
  }, [selectedTicket, ticket, conversations, ninjaoneUsers]);

  // Helper to extract user name from various possible data structures
  const extractUserName = (userData: any): string | null => {
    if (!userData) return null;

    // Direct name property
    if (userData.name) return userData.name;

    // Nested name properties
    if (userData.user?.name) return userData.user.name;
    if (userData.assignedUser?.name) return userData.assignedUser.name;
    if (userData.requester?.name) return userData.requester.name;

    // Display name properties
    if (userData.displayName) return userData.displayName;
    if (userData.user?.displayName) return userData.user.displayName;

    // First and last name combination
    if (userData.firstName && userData.lastName) {
      return `${userData.firstName} ${userData.lastName}`;
    }
    if (userData.user?.firstName && userData.user?.lastName) {
      return `${userData.user.firstName} ${userData.user.lastName}`;
    }

    // Email as fallback
    if (userData.email) return userData.email;
    if (userData.user?.email) return userData.user.email;

    return null;
  };

  // Create a map of user IDs to names from NinjaOne users
  const userMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    if (Array.isArray(ninjaoneUsers)) {
      console.log("NinjaOne users data:", ninjaoneUsers);
      ninjaoneUsers.forEach((user: any) => {
        const userName = extractUserName(user);
        if (!userName) return;

        // Map by ID
        if (user.id) {
          map[String(user.id)] = userName;
        }
        // Map by UID
        if (user.uid) {
          map[String(user.uid)] = userName;
        }
        // Map by email (sometimes used as identifier)
        if (user.email) {
          map[String(user.email)] = userName;
        }
        // Map by username (if available)
        if (user.username) {
          map[String(user.username)] = userName;
        }
        // Map by displayName (if available)
        if (user.displayName) {
          map[String(user.displayName)] = userName;
        }
      });
    }
    console.log("User map created:", map);
    return map;
  }, [ninjaoneUsers]);

  // Helper to get status display name
  const getStatusDisplay = (status: any) => {
    if (!status) return "-";
    if (typeof status === "object" && status.displayName)
      return status.displayName;
    if (typeof status === "string")
      return status.charAt(0).toUpperCase() + status.slice(1);
    return String(status);
  };

  // Helper to get priority display
  const getPriorityDisplay = (priority: any) => {
    if (!priority) return "-";
    return String(priority).charAt(0).toUpperCase() + String(priority).slice(1);
  };

  // Helper to get assigned user (id only for now)
  const getAssignedTo = (ticket: any) => {
    if (!ticket.assignedAppUserId) return "-";

    // Try to get name from user map
    const assignedUserName = userMap[String(ticket.assignedAppUserId)];
    if (assignedUserName) {
      return assignedUserName;
    }

    // Try to extract from ticket data
    const extractedName = extractUserName(
      ticket.assignedAppUser || ticket.assignedUser
    );
    if (extractedName) {
      return extractedName;
    }

    // Fallback to ID
    return `User ID: ${ticket.assignedAppUserId}`;
  };

  // Helper to get requester (uid only for now)
  const getRequester = (ticket: any) => {
    if (!ticket.requesterUid) return "-";

    // Try to get name from user map
    const requesterName = userMap[String(ticket.requesterUid)];
    if (requesterName) {
      return requesterName;
    }

    // Try to extract from ticket data
    const extractedName = extractUserName(
      ticket.requester || ticket.requesterUser
    );
    if (extractedName) {
      return extractedName;
    }

    // Fallback to UID
    return `UID: ${ticket.requesterUid}`;
  };

  // Build a map of contactId/contactUid to name from all messages
  const contactIdToName = React.useMemo(() => {
    const map: Record<string, string> = {};
    conversations.forEach((msg: any) => {
      // From changeDiff
      if (
        msg.appUserContactType === "TECHNICIAN" &&
        msg.changeDiff?.assignedAppUserId?.new?.name &&
        msg.appUserContactId
      ) {
        map[String(msg.appUserContactId)] =
          msg.changeDiff.assignedAppUserId.new.name;
      }
      if (
        msg.appUserContactType === "END_USER" &&
        msg.changeDiff?.requesterUid?.new?.name &&
        msg.appUserContactId
      ) {
        map[String(msg.appUserContactId)] =
          msg.changeDiff.requesterUid.new.name;
      }
      // Also try with appUserContactUid
      if (
        msg.appUserContactType === "TECHNICIAN" &&
        msg.changeDiff?.assignedAppUserId?.new?.name &&
        msg.appUserContactUid
      ) {
        map[String(msg.appUserContactUid)] =
          msg.changeDiff.assignedAppUserId.new.name;
      }
      if (
        msg.appUserContactType === "END_USER" &&
        msg.changeDiff?.requesterUid?.new?.name &&
        msg.appUserContactUid
      ) {
        map[String(msg.appUserContactUid)] =
          msg.changeDiff.requesterUid.new.name;
      }
    });
    console.log("Contact ID to name map:", map);
    return map;
  }, [conversations]);

  // Helper to get the correct contact name for a conversation message
  const getContactName = (msg: any) => {
    console.log("Getting contact name for message:", {
      appUserContactType: msg.appUserContactType,
      appUserContactId: msg.appUserContactId,
      appUserContactUid: msg.appUserContactUid,
      changeDiff: msg.changeDiff,
    });

    // 1. From changeDiff
    if (
      msg.appUserContactType === "TECHNICIAN" &&
      msg.changeDiff?.assignedAppUserId?.new?.name
    ) {
      console.log(
        "Found name from changeDiff (TECHNICIAN):",
        msg.changeDiff.assignedAppUserId.new.name
      );
      return msg.changeDiff.assignedAppUserId.new.name;
    }
    if (
      msg.appUserContactType === "END_USER" &&
      msg.changeDiff?.requesterUid?.new?.name
    ) {
      console.log(
        "Found name from changeDiff (END_USER):",
        msg.changeDiff.requesterUid.new.name
      );
      return msg.changeDiff.requesterUid.new.name;
    }

    // 2. From lookup map
    if (msg.appUserContactId && contactIdToName[String(msg.appUserContactId)]) {
      console.log(
        "Found name from contactIdToName (ID):",
        contactIdToName[String(msg.appUserContactId)]
      );
      return contactIdToName[String(msg.appUserContactId)];
    }
    if (
      msg.appUserContactUid &&
      contactIdToName[String(msg.appUserContactUid)]
    ) {
      console.log(
        "Found name from contactIdToName (UID):",
        contactIdToName[String(msg.appUserContactUid)]
      );
      return contactIdToName[String(msg.appUserContactUid)];
    }

    // 3. From NinjaOne user map
    if (msg.appUserContactId && userMap[String(msg.appUserContactId)]) {
      console.log(
        "Found name from userMap (ID):",
        userMap[String(msg.appUserContactId)]
      );
      return userMap[String(msg.appUserContactId)];
    }
    if (msg.appUserContactUid && userMap[String(msg.appUserContactUid)]) {
      console.log(
        "Found name from userMap (UID):",
        userMap[String(msg.appUserContactUid)]
      );
      return userMap[String(msg.appUserContactUid)];
    }

    // 4. From ticket requester/assignedAppUserId
    if (
      msg.appUserContactType === "END_USER" &&
      ticket?.requester &&
      (ticket.requester.id === msg.appUserContactId ||
        ticket.requester.uid === msg.appUserContactUid)
    ) {
      console.log("Found name from ticket requester:", ticket.requester.name);
      return ticket.requester.name;
    }
    if (
      msg.appUserContactType === "TECHNICIAN" &&
      ticket?.assignedAppUser &&
      (ticket.assignedAppUser.id === msg.appUserContactId ||
        ticket.assignedAppUser.uid === msg.appUserContactUid)
    ) {
      console.log(
        "Found name from ticket assignedAppUser:",
        ticket.assignedAppUser.name
      );
      return ticket.assignedAppUser.name;
    }

    // 5. Fallback
    console.log("Using fallback name:", msg.appUserContactType || "User");
    return msg.appUserContactType || "User";
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Box sx={{ p: 3, color: "error.main" }}>Error: {error}</Box>;
  }

  if (!ticket) {
    return <Box sx={{ p: 3 }}>Ticket not found.</Box>;
  }

  const renderConversationNotesContent = () => (
    <Box
      sx={{
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        overflowX: "hidden",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Conversation Section — min height keeps a usable scroll region when the ticket panel is short (stacked layout + tall chat). */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          background: "#FFF",
          p: 2,
          flex: "1 1 50%",
          minHeight: "clamp(116px, 18vh, 220px)",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#F7F8FA",
            borderRadius: "6px",
            width: "114px",
            height: "33px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 1.5,
            mb: 2,
            boxShadow: "0px 2px 8px 0px rgba(16, 30, 54, 0.08)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: "16px",
              color: "#19213D",
              m: 0,
            }}
          >
            Conversation
          </Typography>
        </Box>
        <Paper
          elevation={0}
          sx={{
            flex: "1 1 0%",
            minHeight: 0,
            p: 2,
            overflowY: "auto",
            overflowX: "hidden",
            minWidth: 0,
          }}
        >
          {conversations?.length > 0 ? (
            conversations.map((msg: any, index: number) => (
              <Box
                key={msg.id || index}
                sx={{
                  mb: 2,
                  pb: 2,
                  borderBottom:
                    index < conversations.length - 1
                      ? "1px dotted #eee"
                      : "none",
                  display: "flex",
                  alignItems: "flex-start",
                  backgroundColor:
                    msg.type === "DESCRIPTION" ? "#f3f0ff" : "inherit",
                  borderRadius: msg.type === "DESCRIPTION" ? 2 : 0,
                  p: msg.type === "DESCRIPTION" ? 2 : 0,
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <UserAvatar
                  name={getContactName(msg)}
                  size={24}
                  initials={getContactName(msg)[0]}
                  bgColor={msg.type === "DESCRIPTION" ? "#8a2be2" : "#bdbdbd"}
                  sx={{ mr: 1.5 }}
                />
                <Box sx={{ flexGrow: 1, mt: 0.2, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: "600",
                        fontSize: "14px",
                        mr: 1,
                        color: "#19213D",
                      }}
                    >
                      {getContactName(msg)}
                    </Typography>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ height: 16, ml: 1, mr: 1, bgcolor: "#F0F2F5" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontWeight: "500",
                        fontSize: "12px",
                        color: "#666F8D",
                      }}
                    >
                      {msg.createTime
                        ? new Date(msg.createTime).toLocaleString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      fontWeight: "400",
                      fontSize: "16px",
                      fontFamily: "var(--theo-font-sans)",
                      color: "#666F8D",
                      ...wrappedHtmlContentSx,
                    }}
                  >
                    {renderHtmlContent(msg.htmlBody || msg.body || "")}
                  </Box>
                </Box>
              </Box>
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 4 }}
            >
              No conversation history available.
            </Typography>
          )}
        </Paper>
      </Box>

      {/* Notes Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          background: "#FFF",
          p: 2,
          flex: "1 1 50%",
          minHeight: "clamp(116px, 18vh, 220px)",
          borderTop: "1px solid #F0F2F5",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#F7F8FA",
            borderRadius: "6px",
            width: "114px",
            height: "33px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 1.5,
            mb: 2,
            boxShadow: "0px 2px 8px 0px rgba(16, 30, 54, 0.08)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: "16px",
              color: "#19213D",
              m: 0,
            }}
          >
            Notes
          </Typography>
        </Box>
        <Paper
          elevation={0}
          sx={{
            flex: "1 1 0%",
            minHeight: 0,
            p: 2,
            overflowY: "auto",
            overflowX: "hidden",
            minWidth: 0,
          }}
        >
          {notes?.length > 0 ? (
            notes.map((note: Note, index: number) => (
              <Box
                key={note.noteId}
                sx={{
                  mb: 2,
                  pb: 2,
                  borderBottom:
                    index < notes.length - 1
                      ? "1px dotted #eee"
                      : "none",
                  display: "flex",
                  alignItems: "flex-start",
                  backgroundColor: "#f5f5f5",
                  borderRadius: 2,
                  p: 2,
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <UserAvatar
                  name={note.user?.name || "User"}
                  size={24}
                  initials={note.user?.name?.[0] || "U"}
                  bgColor="#ff9800"
                  sx={{ mr: 1.5 }}
                />
                <Box sx={{ flexGrow: 1, mt: 0.2, minWidth: 0 }}>
                  <Box
                    sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: "600",
                          fontSize: "14px",
                          mr: 1,
                          color: "#19213D",
                        }}
                      >
                        {note.user?.name || "Unknown User"}
                      </Typography>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ height: 16, ml: 1, mr: 1, bgcolor: "#F0F2F5" }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          fontWeight: "500",
                          fontSize: "12px",
                          color: "#666F8D",
                        }}
                      >
                        {new Date(note.time).toLocaleString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </Box>
                    {(() => {
                      const badge = getNoteVisibilityBadge(note.publish);
                      return (
                        <Chip
                          label={badge.label}
                          size="small"
                          sx={{
                            height: "20px",
                            fontSize: "10px",
                            fontFamily: "var(--theo-font-sans)",
                            fontWeight: "500",
                            backgroundColor: badge.color,
                            color: "#FFFFFF",
                            "& .MuiChip-label": {
                              px: 1,
                            },
                          }}
                        />
                      );
                    })()}
                  </Box>
                  <Box
                    sx={{
                      fontWeight: "400",
                      fontSize: "16px",
                      fontFamily: "var(--theo-font-sans)",
                      color: "#666F8D",
                      ...wrappedHtmlContentSx,
                    }}
                  >
                    {renderHtmlContent(note.content)}
                  </Box>
                </Box>
              </Box>
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 4 }}
            >
              No notes available.
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );

  // Resizable horizontal layout:
  // left side = conversation/notes stack, right side = ticket properties.
  return (
    <Box
      sx={{
        flexGrow: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        overflowY: "auto",
        minHeight: 0,
      }}
    >
      {/* Header — navbar: back • title • optional “focus chat” collapse (supervisor split). */}
      <Box sx={{ p: 2, background: "#FFF", borderBottom: "1px solid #F0F2F5" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 0.5,
            width: "100%",
            gap: 0.5,
          }}
        >
          <IconButton
            onClick={onBack}
            sx={{
              width: 36,
              height: 36,
              minWidth: 36,
              minHeight: 36,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 0.5,
              p: 0,
              transition: "background 0.2s",
              "&:hover": {
                backgroundColor: "#f0f0f0",
              },
            }}
          >
            <img
              src={ArrowBackIcon}
              alt="Arrow Back"
              style={{ width: 20, height: 20 }}
            />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "var(--theo-font-sans)",
                fontWeight: "700",
                fontSize: 18,
                color: "text.primary",
              }}
            >
              Ticket {ticket.id}
            </Typography>
            {(selectedTicket?.superopsUrl || selectedTicket?.autotaskUrl) ? (
              <Typography
                component="a"
                href={selectedTicket.superopsUrl || selectedTicket.autotaskUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="subtitle1"
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontWeight: "500",
                  fontSize: "18px",
                  color: "primary.main",
                  textDecoration: "none",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {ticket.subject || "-"}
              </Typography>
            ) : (
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontWeight: "500",
                  fontSize: "18px",
                  color: "text.secondary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {ticket.subject || "-"}
              </Typography>
            )}
          </Box>
          <TicketQualityReviewPanel
            ticketId={ticketId}
            payload={{
              ticket: (ticket || null) as unknown as Record<string, unknown> | null,
              conversations,
              notes,
            }}
            customerId={
              ticket && typeof ticket === "object"
                ? ((ticket as Record<string, unknown>).customer_id as number | string | null) ?? null
                : null
            }
          />
          {onCollapseForChat ? (
            <Tooltip title="Focus chat (hide conversation & properties)">
              <IconButton
                size="small"
                onClick={onCollapseForChat}
                aria-label="Focus chat, hide ticket details"
                sx={{
                  flexShrink: 0,
                  backgroundColor: "#F5F5F5",
                  "&:hover": { backgroundColor: "#E0E0E0" },
                }}
              >
                {chatPlacement === "below" ? (
                  <KeyboardArrowDownIcon sx={{ fontSize: 22 }} />
                ) : (
                  <ChevronRightIcon sx={{ fontSize: 22 }} />
                )}
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "hidden" }}>
        {propertiesExpanded ? (
          <PanelGroup direction="horizontal">
            <Panel defaultSize={66.67} minSize={20}>
              {renderConversationNotesContent()}
            </Panel>
            <PanelResizeHandle
              style={{
                width: "4px",
                background: "#F0F2F5",
              }}
            />
            <Panel defaultSize={33.33} minSize={12}>
            <Box
              sx={{
                height: "100%",
                minWidth: 0,
                background: "#FFF",
                p: 0,
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Toggle Button */}
              <IconButton
                onClick={() => setPropertiesExpanded(false)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 44,
                  zIndex: 10,
                  backgroundColor: "#F5F5F5",
                  "&:hover": {
                    backgroundColor: "#E0E0E0",
                  },
                  width: 32,
                  height: 32,
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </IconButton>

            <PanelGroup direction="vertical">
              <TicketDetailSection title="Ticket Properties" panelId="ticket-properties" order={1}
                actions={(isRefreshing || localRefreshing) ? <TypingIndicator /> : undefined}>
            {/* Status */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: "#7B849E",
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            Status
          </Typography>
          {ticket.status && (
            <Chip
              label={getStatusDisplay(ticket.status)}
              sx={{
                background: "#E0E0E0",
                color: "#333",
                fontWeight: 700,
                borderRadius: 3,
                px: 1.5,
                height: 28,
                fontSize: 15,
                boxShadow: "none",
                ml: 2,
              }}
            />
          )}
        </Box>
        {/* Assigned to */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: "#7B849E",
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            Assigned to
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              color: "#0B0D1E",
              fontSize: 12,
              fontFamily: "inter",
              ml: 2,
            }}
          >
            {getAssignedTo(ticket)}
          </Typography>
        </Box>
        {/* Requested by */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: "#7B849E",
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            Requested by
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              color: "#0B0D1E",
              fontSize: 12,
              fontFamily: "inter",
              ml: 2,
            }}
          >
            {getRequester(ticket)}
          </Typography>
        </Box>
        {/* Start date */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: "#7B849E",
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            Start date
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              color: "#0B0D1E",
              fontSize: 12,
              fontFamily: "inter",
              ml: 2,
            }}
          >
            {ticket.createTime
              ? new Date(ticket.createTime).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : "-"}
          </Typography>
        </Box>
        {/* Due date */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: "#7B849E",
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            Due date
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              color: "#0B0D1E",
              fontSize: 12,
              fontFamily: "inter",
              ml: 2,
            }}
          >
            {ticket.dueDate
              ? new Date(ticket.dueDate).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : "-"}
          </Typography>
        </Box>
        {/* Priority */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: "#7B849E",
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            Priority
          </Typography>
          {ticket.priority && (
            <Chip
              label={getPriorityDisplay(ticket.priority)}
              sx={{
                background: "#E0E0E0",
                borderRadius: 2,
                height: 28,
                px: 1.5,
                boxShadow: "none",
                ml: 2,
              }}
            />
          )}
        </Box>
        {/* Queue (not available in NinjaOne, so skip) */}
              </TicketDetailSection>
              {ticket.device ? (
                <TicketDetailSection title="Device Properties" panelId="ticket-device-properties" order={2} divider>
                  <TicketDeviceProperties device={ticket.device} />
                </TicketDetailSection>
              ) : null}
            </PanelGroup>
            </Box>
            </Panel>
          </PanelGroup>
        ) : (
          <Box sx={{ height: "100%", display: "flex", minWidth: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
              {renderConversationNotesContent()}
            </Box>
            <Box
              sx={{
                width: 48,
                minWidth: 48,
                background: "#FFF",
                borderLeft: "1px solid #F0F2F5",
                position: "relative",
              }}
            >
              <IconButton
                onClick={() => setPropertiesExpanded(true)}
                sx={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  zIndex: 10,
                  backgroundColor: "#F5F5F5",
                  "&:hover": {
                    backgroundColor: "#E0E0E0",
                  },
                  width: 32,
                  height: 32,
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NinjaOneTicketDetail;
